import React, {FC, ReactNode, useEffect, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {useTimeout} from '../../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {useInitialEffect} from '../../../../../libs/shared/core/src/helpers/react/hooks/useEffectHelpers';
import {fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {flexCenter} from '../../../../../libs/shared/ui/src/styles/mixins';
import illustration from '../../../../assets/images/onboardingWelcomeLoadingIllustration.png';
import {OnboardingGuideSlide} from '../../guide/onboardingGuideSlide';

/*
 * Props.
 */

export interface OnboardingWorkflowsProgressSlideProps {
  stages: Array<StagesEnum>;
  isLoading: boolean;
  onFinished: () => void;
}

/*
 * Constants.
 */

// To avoid showing the 100% progress too fast, we will show it for at least this duration.
const MINIMUM_DELAY_FULL_PROGRESS = 800;

// The amount of time we spend "creating" resources.
const TOTAL_STAGES_DELAY = 3000;

/*
 * Styles.
 */

const StyledWrapper = styled.div`
  width: 100%;
  height: 100%;
  ${flexCenter};
  justify-content: center;
  flex-direction: column;
  gap: 32px;

  color: ${(p) => p.theme.greys.shade70};
  text-align: center;
  font-size: 24px;
  font-weight: ${fontWeights.bold};
`;

const StyledProgressContainer = styled.div`
  width: 348px;
  height: 8px;
  border-radius: 10px;
  background-color: ${(p) => p.theme.greys.shade30};
  overflow: hidden;
`;

const StyledProgress = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #ff2b73 0%, #ff6c35 100%);
  transition: width 800ms ease-out;
`;

/*
 * Component.
 */

export const OnboardingWorkflowsProgressSlide: FC<OnboardingWorkflowsProgressSlideProps> = ({
  stages,
  isLoading,
  onFinished,
}) => {
  const [currentStage, setStage] = useState<StagesEnum>(StagesEnum.TAGS);
  const [safeSetTimeout] = useTimeout();

  const sortedStages = [...stages];
  sortedStages.sort((a, b) => STAGE_ORDER.indexOf(a) - STAGE_ORDER.indexOf(b));

  const stageDelay = TOTAL_STAGES_DELAY / (sortedStages.length || 1);

  useInitialEffect(() => {
    function updateProgress() {
      setStage((current) => {
        const nextStageIndex = sortedStages.indexOf(current) + 1;
        if (nextStageIndex > sortedStages.length - 1) {
          return current;
        }
        const nextStage = sortedStages[nextStageIndex];
        safeSetTimeout(updateProgress, stageDelay);
        return nextStage;
      });
    }

    safeSetTimeout(updateProgress, stageDelay);
  });

  useEffect(() => {
    if (!isLoading && currentStage === sortedStages[sortedStages.length - 1]) {
      safeSetTimeout(onFinished, MINIMUM_DELAY_FULL_PROGRESS);
    }
  }, [isLoading, currentStage, onFinished, safeSetTimeout]);

  const progress = getProgressForStage(currentStage, isLoading);

  return (
    <OnboardingGuideSlide navPrimary={null}>
      <StyledWrapper>
        <img width="348" height="318" alt="" src={illustration} />
        <span>{STAGE_LABELS[currentStage]}</span>
        <StyledProgressContainer>
          <StyledProgress style={{width: `${progress * 100}%`}} />
        </StyledProgressContainer>
      </StyledWrapper>
    </OnboardingGuideSlide>
  );
};

/*
 * Helpers.
 */

export enum StagesEnum {
  INBOX = 'inbox',
  TAGS = 'tags',
  RULES = 'rules',
}

const STAGE_ORDER = [StagesEnum.INBOX, StagesEnum.TAGS, StagesEnum.RULES];

const STAGE_LABELS: Record<StagesEnum, ReactNode> = {
  [StagesEnum.INBOX]: (
    <Trans id="onboardingwelcome-workflows-inbox-creation-progress">
      Preparing your shared inbox&hellip;
    </Trans>
  ),
  [StagesEnum.TAGS]: (
    <Trans id="onboardingwelcome-workflows-inbox-creation-progress">Generating tags&hellip;</Trans>
  ),
  [StagesEnum.RULES]: (
    <Trans id="onboardingwelcome-workflows-inbox-creation-progress">Creating rules&hellip;</Trans>
  ),
};

/* Based on a 3 seconds total load time */
function getProgressForStage(stage: StagesEnum, isLoading: boolean): number {
  switch (stage) {
    case StagesEnum.INBOX:
      return 0.1;
    case StagesEnum.TAGS:
      return 0.3;
    case StagesEnum.RULES:
    default:
      return isLoading ? 0.9 : 1;
  }
}
